import validator from "validator";
import { get } from "helpers/api";
import { useFormik } from "formik";
import { AuthAPI } from "apis/v2/auth";
import { PageProps } from "pages/_app";
import { useRouter } from "next/router";
import { API_URL } from "helpers/common";
import useUserInfo from "hooks/user-info";
import { useEffect, useState } from "react";
import useNewSessionStorage from "hooks/new-session-storage";
import AppLinkText from "components/v2/atoms/app-link-text/AppLinkText";
import useAppDialog from "components/v2/organisms/app-dialog/AppDialog.hook";
import V2Container, { V2ContainerProps } from "components/v2/templates/V2Container";
import AppButton, { AppButtonProps } from "components/v2/atoms/app-button/AppButton";
import AppForm, { AppFormError, AppFormProps } from "components/v2/organisms/app-form/AppForm";
import XeroButton, { XeroButtonProps } from "components/v2/templates/instant-vat/others/XeroButton";
import MagicLinkDialog, { MagicLinkDialogProps } from "components/v2/templates/auth/MagicLinkDialog";
import AppCheckBoxes, { AppCheckBoxesProps } from "components/v2/molecules/app-check-boxes/AppCheckBoxes";
import { InputAdornment, Stack, Typography, TypographyProps, useMediaQuery, useTheme } from "@mui/material";
import AppXeroButton, { AppXeroButtonProps } from "components/v2/templates/instant-vat/others/AppXeroButton";
import AppSocialButton, { AppSocialButtonProps } from "components/v2/atoms/app-social-button/AppSocialButton";
import AppIntuitButton, { AppIntuitButtonProps } from "components/v2/templates/instant-vat/others/AppIntuitButton";

export interface LoginFormValues {
    email: string;
    password: string;
}

export interface RememberFormValues {
    remember: boolean;
}

enum SOCIAL_ACCOUNT {
    GOOGLE = "Google",
    INTUIT = "Intuit",
    MICROSOFT = "Microsoft",
    XERO = "Xero",
}

declare const window: {
    // heap: {
    //     identify: (email: string) => void;
    // };
    smartlook: (...args: any[]) => void;
};

function Login(props: PageProps) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"), {
        defaultMatches: true,
    });

    const router = useRouter();

    const magicLinkDialog = useAppDialog();

    const [error, setError] = useState<string>("");

    const [showPassword, setShowPassword] = useState(false);

    const [failedLoginAttempts, setFailedLoginAttempts] = useState(0);

    const [redirectPathFromSession] = useNewSessionStorage<string | null>("authRedirectPath", null);

    const [socialAccount, setSocialAccount] = useState<SOCIAL_ACCOUNT>();

    const { fetchUserInfo } = useUserInfo({
        pageProps: props,
        fetchOnInit: false,
    });

    const [hydrated, setHydrated] = useState(false);
    const [redirectToSend, setRedirectToSend] = useState<string | undefined>(undefined);

    useEffect(() => {
        setHydrated(true);
        if (redirectPathFromSession) {
            setRedirectToSend(redirectPathFromSession);
        }
    }, [redirectPathFromSession]);

    const methods = {
        validateEmail(email: string) {
            let error;

            if (!email || validator.isEmpty(email, { ignore_whitespace: true })) {
                error = "Please enter a valid email address";
            } else if (!validator.isEmail(email)) {
                error = "Please enter a valid email address";
            }

            return error;
        },
        validatePassword(password: string) {
            let error;

            if (validator.isEmpty(password, { ignore_whitespace: true })) {
                error = "Please enter a password";
            } else if (password.trim().length < 6) {
                error = "Please enter atleast 6 characters";
            }

            return error;
        },
    };

    const v2ContainerProps: V2ContainerProps = {
        stackProps: {
            sx: {
                gap: 2,
                minHeight: {
                    xs: "calc(100vh - 32px)",
                    sm: "calc(100vh - 130px)",
                },
            },
        },
        heading: "Login",
        subHeading: "Log into your Adsum account",
    };

    const ssoStackProps = {
        sx: {
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
        },
    };

    const ssoMBStackProps = {
        sx: {
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
        },
    };

    const formik = useFormik<LoginFormValues>({
        initialValues: {
            email: "",
            password: "",
        },
        onSubmit(values, { setSubmitting }) {
            setError("");

            AuthAPI.login({
                ...values,
                remember,
                redirect_url: redirectToSend,
            })
                .then(async response => {
                    const { data } = response;

                    if (data.type === "mfa_required") {
                        // Store MFA data and redirect to MFA page
                        sessionStorage.setItem(
                            "mfa_data",
                            JSON.stringify({
                                user_id: data.user_id,
                                company_id: data.company_id,
                                available_methods: data.available_methods,
                            }),
                        );
                        router.push("/v2/auth/mfa");
                        return;
                    }

                    if (data.type === "user") {
                        // if (window.heap) {
                        //     window.heap.identify(values.email);
                        // }

                        try {
                            if (typeof window.smartlook === "function") {
                                const loggedInUser = await fetchUserInfo();
                                const companiesList = await get("/user-companies");

                                window.smartlook("identify", values.email, {
                                    name: `${loggedInUser?.first_name}${
                                        loggedInUser?.last_name ? ` ${loggedInUser?.last_name}` : ""
                                    }`,
                                    email: values.email,
                                    company: companiesList[0]?.company_name,
                                });
                            }

                            location.href = API_URL + "/home";
                        } catch (error) {
                            console.log(`Error`, error);
                        }
                    }
                })
                .catch(error => {
                    const { status, data } = error.response;

                    if (status === 404) {
                        router.push("/get-started/welcome");
                    } else if (status === 401) {
                        const { sso_details } = data;

                        if (sso_details === "Google") {
                            setSocialAccount(SOCIAL_ACCOUNT.GOOGLE);
                            setError("It looks like you logged in with Google last time. Please try again.");
                        } else if (sso_details === "Microsoft") {
                            setSocialAccount(SOCIAL_ACCOUNT.MICROSOFT);
                            setError("It looks like you logged in with Microsoft last time. Please try again.");
                        } else if (sso_details === "Intuit") {
                            setSocialAccount(SOCIAL_ACCOUNT.INTUIT);
                            setError("It looks like you logged in with Intuit last time. Please try again.");
                        } else if (sso_details === "Xero") {
                            setSocialAccount(SOCIAL_ACCOUNT.XERO);
                            setError("It looks like you logged in with Xero last time. Please try again.");
                        } else {
                            setFailedLoginAttempts(prev => prev + 1);
                            setError("Invalid password");
                        }
                    } else {
                        setError(error.message);
                    }

                    setSubmitting(false);
                });
        },
    });

    const togglePasswordVisibilityTextProps: TypographyProps = {
        children: showPassword ? "Hide" : "Show",
        variant: "body2",
        sx: {
            fontWeight: 500,
            cursor: "pointer",
            userSelect: "none",
            color: "text.primary",
            textDecoration: "underline",
            "&:hover": {
                textDecoration: "none",
            },
        },
        onClick: () => {
            setShowPassword(prev => !prev);
        },
    };

    const loginFormProps: AppFormProps<LoginFormValues> = {
        formik,
        fields: [
            {
                name: "email",
                type: "email",
                label: "Email",
                validate: methods.validateEmail,
            },
            {
                name: "password",
                type: showPassword ? "text" : "password",
                label: "Password",
                validate: methods.validatePassword,
                InputProps: {
                    notched: false,
                    endAdornment: hydrated ? (
                        <InputAdornment position="end">
                            <Typography {...togglePasswordVisibilityTextProps} />
                        </InputAdornment>
                    ) : null,
                },
            },
        ],
    };

    const forgotLinkProps: TypographyProps = {
        id: "login_forgot",
        children: "Forgot password?",
        sx: {
            alignSelf: "center",
        },
        onClick() {
            router.push("/v2/auth/forgot-password");
        },
    };

    const rememberCheckboxProps: AppCheckBoxesProps<RememberFormValues> = {
        options: [
            {
                value: "remember",
                label: "Keep me logged in for 7 days",
            },
        ],
        formik: useFormik({
            initialValues: {
                remember: true,
            } as RememberFormValues,
            onSubmit() {
                // Do nothing
            },
        }),
        formControlProps: {
            sx: {
                alignSelf: "center",
            },
            disabled: formik.isSubmitting,
        },
    };

    const remember = rememberCheckboxProps.formik.values.remember;

    const continueButtonProps: AppButtonProps = {
        id: "login_email",
        children: "Login",
        disabled: formik.isSubmitting,
        onClick: formik.submitForm,
    };

    const orTextProps: TypographyProps = {
        sx: {
            alignSelf: "center",
        },
        children: "or",
    };

    const googleButtonProps: AppSocialButtonProps = {
        brand: "google",
        id: "login_google",
        onClick() {
            let url = API_URL + "/google_login";

            if (remember) {
                url += "?remember=true";
            }

            if (redirectToSend) {
                if (remember) {
                    url += `&redirect_url=${redirectToSend}`;
                } else {
                    url += `?redirect_url=${redirectToSend}`;
                }
            }

            location.href = url;
        },
    };

    const microsoftButtonProps: AppSocialButtonProps = {
        brand: "microsoft",
        id: "login_microsoft",
        onClick() {
            let url = API_URL + "/microsoft_login";

            if (remember) {
                url += "?remember=true";
            }

            if (redirectToSend) {
                if (remember) {
                    url += `&redirect_url=${redirectToSend}`;
                } else {
                    url += `?redirect_url=${redirectToSend}`;
                }
            }

            location.href = url;
        },
    };

    const intuitButtonProps: AppIntuitButtonProps = {
        id: "login_intuit",
        onClick() {
            let url = API_URL + `/intuit_login`;
            if (redirectToSend) {
                url += `?redirect_url=${redirectToSend}`;
            }

            location.href = url;
        },
        isMobile: isMobile,
    };

    const xeroButtonProps: XeroButtonProps = {
        appButtonProps: {
            id: "login_xero",
            onClick() {
                let url = API_URL + "/xero_login";

                if (redirectToSend) {
                    url += `?redirect_url=${redirectToSend}`;
                }

                location.href = url;
            },
        },
        isMobile,
    };

    const getStartedLinkProps: TypographyProps = {
        id: "login_get-started",
        children: "Don't have an account?",
        sx: {
            marginBottom: {
                xs: 5,
                sm: 0,
            },
            alignSelf: "center",
        },
        onClick() {
            location.href = "https://www.adsum-works.com/";
        },
    };

    const magicLinkDialogProps: MagicLinkDialogProps = {
        dialog: magicLinkDialog,
        async requestMagicLink() {
            const { email } = formik.values;

            return AuthAPI.requestMagicLink(email)
                .then(() => true)
                .catch(error => {
                    magicLinkDialog.close();
                    props.showAlert(error.message);
                    return false;
                });
        },
    };

    useEffect(() => {
        if (failedLoginAttempts > 1) {
            magicLinkDialog.open();
        }
    }, [failedLoginAttempts]);

    useEffect(() => {
        setError("");
    }, [formik.values.email, formik.values.password]);

    return (
        <V2Container {...v2ContainerProps}>
            <AppForm {...loginFormProps} />
            {error && <AppFormError error={error} />}
            <AppLinkText {...forgotLinkProps} />
            <AppCheckBoxes {...rememberCheckboxProps} />
            <Stack gap={1}>
                {socialAccount ? (
                    socialAccount === SOCIAL_ACCOUNT.GOOGLE ? (
                        <AppSocialButton {...googleButtonProps} />
                    ) : socialAccount === SOCIAL_ACCOUNT.MICROSOFT ? (
                        <AppSocialButton {...microsoftButtonProps} />
                    ) : socialAccount === SOCIAL_ACCOUNT.INTUIT ? (
                        <AppIntuitButton isFilled {...intuitButtonProps} />
                    ) : socialAccount === SOCIAL_ACCOUNT.XERO ? (
                        <XeroButton {...xeroButtonProps} />
                    ) : null
                ) : (
                    <>
                        <AppButton {...continueButtonProps} />
                        <Typography {...orTextProps} />
                        <AppSocialButton {...googleButtonProps} />
                        <AppSocialButton {...microsoftButtonProps} />
                        {isMobile ? (
                            <Stack {...ssoMBStackProps}>
                                <XeroButton {...xeroButtonProps} />
                                <AppIntuitButton {...intuitButtonProps} />
                            </Stack>
                        ) : (
                            <Stack {...ssoStackProps}>
                                <XeroButton {...xeroButtonProps} />
                                <AppIntuitButton {...intuitButtonProps} />
                            </Stack>
                        )}
                    </>
                )}
            </Stack>
            <span style={{ flex: 1 }} />
            <AppLinkText {...getStartedLinkProps} />
            <MagicLinkDialog {...magicLinkDialogProps} />
        </V2Container>
    );
}

export default Login;
